import { gehoertMirImplizit } from '@netcon-work/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CalendarDays, List, Target } from 'lucide-react'
import { useSearch } from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'
import { HeuteNichtDa, useAbwesenheiten } from '../components/Abwesenheit'
import { AufgabeAnlegen } from '../components/AufgabeAnlegen'
import { SchnelleAufgabe } from '../components/SchnelleAufgabe'

import { AufDemSchirm } from '../components/meintag/AufDemSchirm'
import { Geschafft } from '../components/meintag/Geschafft'
import { Jetzt } from '../components/meintag/Jetzt'
import { KlopftAn } from '../components/meintag/KlopftAn'
import { useMeinTagLage } from '../components/meintag/lage'
import { AlsNaechstes } from '../components/meintag/AlsNaechstes'
import { Nachrichten } from '../components/meintag/Nachrichten'
import { CirclePipe } from '../components/meintag/CirclePipe'
import { PulsUmfeld, type CircleAnsicht } from '../components/meintag/Puls'
import { TagesplanAnsicht } from '../components/meintag/TagesplanAnsicht'
import { useTagesmodus, type Tagesmodus } from '../components/meintag/modus'
import { useMerkerKennung } from '../lib/merker'
import { RuhigGestellt } from '../components/meintag/RuhigGestellt'
import { DaumenKnopf, MeinTagTelefon } from '../components/meintag/Telefon'
import { TermineHeute } from '../components/meintag/TermineHeute'
import { Vorlauf } from '../components/meintag/Vorlauf'
import { kalenderTagVormerken } from '../lib/markierung'
import { pulsOrtMerken } from '../lib/puls'

import { Tagesziel } from '../components/meintag/Tagesziel'
import { VorhabenSpuren } from '../components/meintag/VorhabenSpur'
import { Topbar } from '../components/Topbar'
import { WasAlsNaechstes } from '../components/WasAlsNaechstes'
import { VorhabenDetail } from '../components/VorhabenDetail'
import { Button } from '../components/ui/primitives'
import { cn } from '../lib/cn'
import { useGelesenSetzen } from '../lib/gelesen'
import { useTelefonLayout } from '../lib/medien'
import { useStarten } from '../lib/mutationen'
import { zaehlen } from '../lib/nutzung'
import { sitzungQuery, workspacesQuery } from '../lib/queries'
import { trpc } from '../lib/trpc'
import { EntwurfHinweis } from '../components/claude/EntwurfHinweis'

/**
 * **Mein Tag** (Konzept Kap. 5.1, umgebaut in [Z-02](../../../../docs/phasen/zusatz-02-mein-tag.md)).
 *
 * ## Was sich geändert hat und warum
 *
 * Bis zum 21.08.2026 listete diese Ansicht nach **Zustand**: *In Arbeit*, *Für dich*, *Neu für
 * dich*, *Wartet*, *Geschafft* – und daneben stand in der halben Breite der Vorrat. Formal
 * richtig, aber es zeigte Zustände statt Absichten. Niemand denkt morgens in Zuständen; man
 * denkt: *„ich schiebe zwei Vorhaben nach vorn, und nebenbei kommen Tickets rein."*
 *
 * Nils, 21.08.2026: *„Möglichst einfach, Vorhaben berücksichtigen, übersichtlich, soll Spaß
 * machen."*
 *
 * Jetzt sind es **Zonen mit je einer Absicht**, von oben nach unten:
 *
 * | Zone | Frage |
 * |---|---|
 * | 0 · Klopft an | Was kann nicht bis morgen warten? **Meistens gar nicht da.** |
 * | 1 · Jetzt | Woran arbeite ich gerade? |
 * | 2 · Meine Vorhaben | Was schiebe ich diese Woche voran? **Nur da, wenn es Vorhaben gibt** – siehe `VorhabenSpur.tsx`. |
 * | 3 · Als Nächstes | Was ist als Nächstes dran? **Eigenes und Vorrat in einer Reihenfolge** (Z-06). |
 * | 4 · Geschafft | Was habe ich fertig bekommen? |
 * | 5 · Ruhig gestellt | Was ist da, braucht aber heute nichts von mir? |
 *
 * ## Was **nicht** verloren ging
 *
 * Nichts. Pool-Filter, „★ Für mich vorgesehen", Zeitfenster-Marke, WIP-Sperre, die Rückfrage
 * bei fremdem Wunsch, Zurückgeben, Statuswechsel in der Kachel, der Aktivitätsfeed und die
 * Erledigten der letzten 28 Tage sind alle noch da – sie stehen nur nicht mehr alle
 * gleichzeitig auf derselben Höhe.
 *
 * ## Wo gerechnet wird
 *
 * Nicht hier. Die Lage kommt aus `components/meintag/lage.ts`, die Regeln aus
 * `@netcon-work/shared`. Diese Datei setzt nur zusammen – deshalb ist sie kurz geblieben,
 * obwohl die Ansicht größer geworden ist.
 */
export function MeinTag() {
  const lage = useMeinTagLage()
  const starten = useStarten()
  const gelesenSetzen = useGelesenSetzen()
  const abwesenheiten = useAbwesenheiten()

  const [modus, setModus] = useTagesmodus()
  /**
   * **Rad oder Fokus-Puls** (Z-22). Hier und nicht im Circle gehalten, weil im Puls das Umfeld
   * zurücktritt – die Zeile der Abwesenden und die Termine stehen in dieser Seite, nicht im Rad.
   */
  const [circleAnsicht, setCircleAnsicht] = useState<CircleAnsicht>('rad')
  const [umfeldZeigen, setUmfeldZeigen] = useState(false)
  const imPuls = modus === 'circle' && circleAnsicht === 'puls'
  /** Wer den Puls im Circle bedient, kommt über die Marke oben wieder hierher (Z-24). */
  useEffect(() => {
    if (imPuls) pulsOrtMerken('/')
  }, [imPuls])
  const ruhig = imPuls && !umfeldZeigen
  /** Im Puls: nächster Termin und „Umfeld zeigen" – in der Kopfzeile des Circle, nicht darüber. */
  const pulsUmfeld = imPuls ? (
    <PulsUmfeld zeigen={umfeldZeigen} umschalten={() => setUmfeldZeigen((x) => !x)} />
  ) : null
  const einfach = modus === 'einfach'
  /**
   * **Unter 1024 px eine andere Zusammenstellung** ([Z-16](../../../../docs/phasen/zusatz-16-pwa-mein-tag.md)).
   * Dieselben Zonen, aber als Reiter am unteren Rand statt untereinander – siehe `Telefon.tsx`.
   * Der Modus-Umschalter gilt dort nicht: „Einfach" und „Ausführlich" sind zwei Anordnungen für
   * einen großen Bildschirm, und auf dem Telefon gibt es nur die eine.
   */
  const telefon = useTelefonLayout()
  /** Welche der vier Ansichten gebraucht wird – und auf welchem Gerät (E-410). */
  useEffect(() => {
    zaehlen(`ansicht:meintag/${modus}${telefon ? '/telefon' : ''}`)
  }, [modus, telefon])

  const [anlegenOffen, setAnlegenOffen] = useState(false)
  const [schnellOffen, setSchnellOffen] = useState(false)
  const [ziehenOffen, setZiehenOffen] = useState(false)
  /** Die eigene persönliche Arbeitswelt – der Ort der Schnellen Aufgabe (Z-15, E-278). */
  const { data: workspaces = [], isSuccess: weltenGeladen } = useQuery(workspacesQuery)
  const meineWelt = workspaces.find(gehoertMirImplizit)

  /**
   * **`/?schnell=1` öffnet die Schnelle Aufgabe** – der Weg vom Startbildschirm (Z-16).
   *
   * Das Manifest bietet „Schnelle Aufgabe" als Verknüpfung an (langes Drücken auf das
   * App-Symbol); sie kann nur eine Adresse nennen. Gibt es keine persönliche Welt, öffnet sich
   * stattdessen der große Dialog – ein Sprung ins Leere wäre der eine Fall, den man einem
   * Startbildschirm-Knopf nie verzeiht. Gewartet wird, bis Welten und Sitzung da sind, damit
   * die Entscheidung nicht am Ladezeitpunkt hängt; danach verschwindet der Parameter aus der
   * Adresse, sonst käme der Dialog bei jedem Neuladen wieder.
   */
  const suche = useSearch({ strict: false }) as Record<string, unknown>
  /**
   * **Teilen nach Work** (Z-16): Android reicht über `share_target` im Manifest Titel, Text und
   * Adresse als `?titel=…&text=…&url=…` herein. Daraus wird eine Schnelle Aufgabe mit dem Text
   * als Titel und Adresse als Notiz – vorbelegt, nicht angelegt; der Mensch bestätigt.
   * `String()`, weil der Router Werte, die nach Zahlen aussehen, als Zahlen liest.
   */
  const geteilt = ['titel', 'text', 'url'].some((k) => suche[k] !== undefined)
  const vorbelegt = geteilt ? geteiltesVorbelegen(suche) : null
  const schnellAngefordert = suche.schnell !== undefined || geteilt
  const [schnellBehandelt, setSchnellBehandelt] = useState(false)
  /**
   * **`/?puls=1` führt in den Puls** (Z-22) – der Weg aus der {@link Topbar}-Marke „Zum Puls" auf
   * jeder Seite. Stellt den Circle und die Puls-Ansicht ein und räumt die Adresse wieder auf.
   */
  /** Ein Zeitstempel, kein Schalter: der zweite Klick aus „Mein Tag" heraus muss auch greifen. */
  const pulsAngefordert = suche.puls
  useEffect(() => {
    if (pulsAngefordert === undefined) return
    setModus('circle')
    setCircleAnsicht('puls')
    const adresse = new URL(window.location.href)
    adresse.searchParams.delete('puls')
    window.history.replaceState(window.history.state, '', adresse)
  }, [pulsAngefordert, setModus])
  useEffect(() => {
    if (!schnellAngefordert || schnellBehandelt || !weltenGeladen || !lage.ichId) return
    setSchnellBehandelt(true)
    if (meineWelt) setSchnellOffen(true)
    else setAnlegenOffen(true)
    const adresse = new URL(window.location.href)
    for (const k of ['schnell', 'titel', 'text', 'url']) adresse.searchParams.delete(k)
    if (adresse.href !== window.location.href) {
      window.history.replaceState(window.history.state, '', adresse)
    }
  }, [schnellAngefordert, schnellBehandelt, weltenGeladen, lage.ichId, meineWelt])
  /**
   * **Die Reihenfolge im Fokus ist Ansichtssache – aber eine, die bleibt**
   * ([E-327](../../../docs/ENTSCHEIDUNGEN.md#e-327), 04.09.2026).
   *
   * „Nach vorn" ändert keinen Status und schreibt nichts am Vorgang – es entscheidet nur,
   * welche der laufenden Aufgaben die große Karte bekommt. Deshalb steht die Wahl nicht in der
   * Datenbank.
   *
   * **Bis heute stand sie aber in einem `useState`** und war beim nächsten Neuladen weg. Nils:
   * *„wenn man die Seite neu lädt hat er sich nichts behalten. Das sollte für den User
   * natürlich gespeichert werden."* Wer morgens sagt, woran er sitzt, sagt es nicht dreimal am
   * Tag neu – dieselbe Begründung wie beim Modus-Umschalter eine Zeile weiter oben.
   *
   * Also `localStorage`, wie jede andere Ansichtsvorliebe hier: je Gerät, keine Migration.
   */
  const [vorn, setVorn] = useMerkerKennung('meintag-vorn')
  const [vorhabenDetail, setVorhabenDetail] = useState<string | null>(null)

  /**
   * **Eine Kennung, die nichts mehr trifft, wird vergessen** – nicht behalten und ignoriert.
   * Sonst schleppt der Speicher die Aufgabe von vorletzter Woche mit, und ihr alter Platz
   * käme zurück, sobald sie noch einmal auftaucht.
   */
  const vornGilt = vorn !== null && lage.jetzt.some((a) => a.id === vorn)
  useEffect(() => {
    if (vorn !== null && lage.jetzt.length > 0 && !vornGilt) setVorn(null)
  }, [vorn, vornGilt, lage.jetzt.length, setVorn])

  const laufend = vornGilt
    ? [...lage.jetzt].sort((a, b) => Number(b.id === vorn) - Number(a.id === vorn))
    : lage.jetzt

  const heute = new Date().toLocaleDateString('de-DE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })

  return (
    <>
      <Topbar
        titel="Mein Tag"
        ohnePuls={imPuls}
        /* Auf dem Telefon ohne Kalenderwoche – der Kopf ist dort schon hoch genug (Z-16). */
        untertitel={telefon ? heute : `${heute} · KW ${kalenderwoche(new Date())}`}
        aktion={
          telefon ? (
            /* Anlegen ist der ⚡-Knopf und „Was als Nächstes" steht im Reiter „Vorlauf" (Z-16).
               Geblieben sind das Tagesziel und – seit dem 30.09.2026 – der Ansichtsschalter. */
            <div className="flex items-center gap-2">
              <Tagesziel
                fertig={lage.heuteFertig}
                ziel={lage.ziel}
                serie={lage.serieTage}
                eigenes={lage.eigenesZiel}
                ichId={lage.ichId}
              />
              <TelefonAnsichtSchalter modus={modus} setModus={setModus} />
            </div>
          ) : /**
           * **Im Puls tritt auch der Kopf zurück** (Z-22, 20.10.2026): Tagesziel, Anlegen und
           * „Was als Nächstes" sind Planen, nicht Arbeiten. Es bleiben die Ansicht und ⚡ –
           * einen Gedanken parken, ohne den Puls zu verlassen.
           */
          imPuls ? (
            <div className="flex flex-wrap items-center gap-2">
              <AnsichtSchalterKompakt modus={modus} setModus={setModus} />
              {meineWelt && lage.ichId && (
                <Button
                  variant="ghost"
                  onClick={() => setSchnellOffen(true)}
                  title="Schnelle Aufgabe – einen Gedanken parken, der Puls läuft weiter"
                  aria-label="Schnelle Aufgabe"
                >
                  ⚡
                </Button>
              )}
            </div>
          ) : (
            /* Umbrechend: vier Dinge (Tagesziel, Serie, „+ Aufgabe", „Was als Nächstes")
               brauchen zusammen rund 470 px – auf dem Telefon sind 358 da (Z-10). Unter
               1400 px stehen Ansicht, Ziel und Anlegen kompakt (Z-22, 20.10.2026). */
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <div className="hidden min-[1400px]:block">
                <ModusSchalter modus={modus} setModus={setModus} />
              </div>
              <div className="min-[1400px]:hidden">
                <AnsichtSchalterKompakt modus={modus} setModus={setModus} />
              </div>
              <Tagesziel
                fertig={lage.heuteFertig}
                ziel={lage.ziel}
                serie={lage.serieTage}
                eigenes={lage.eigenesZiel}
                ichId={lage.ichId}
              />
              <AnlegeKnopf
                schnell={meineWelt && lage.ichId ? () => setSchnellOffen(true) : null}
                anlegen={() => setAnlegenOffen(true)}
                weltName={meineWelt?.label ?? null}
              />
              {/**
               * **Ansehen darf man immer** (Nils, 24.08.2026: *„wenn ich darauf drücke erscheint
               * nicht das popup"*).
               *
               * Der Knopf war `disabled`, sobald die drei Fokus-Plätze voll waren – und ein
               * gesperrter Knopf antwortet auf einen Klick mit **gar nichts**. Der Grund stand
               * in einem `title`, den nur sieht, wer schwebt statt zu klicken.
               *
               * Seit [Z-06](../../../../docs/phasen/zusatz-06-nehmen-und-starten.md) ist die Frage
               * ohnehin kleiner geworden: gesperrt ist nur noch **Starten**, nicht das Nehmen.
               * Wer sich versorgen will, kann das auch bei vollem Fokus – das Fenster sagt beides.
               */}
              <Button
                variant="primary"
                onClick={() => setZiehenOffen(true)}
                title="Die Reihenfolge, in der Work dir Arbeit vorschlägt."
              >
                Was als Nächstes
              </Button>
            </div>
          )
        }
      />

      {/* Ein Entwurf von Claude wartet (Z-25) – über jeder Ansicht, auch Kalender und Circle. */}
      {!imPuls && <EntwurfHinweis />}

      {anlegenOffen && <AufgabeAnlegen schliessen={() => setAnlegenOffen(false)} />}
      {schnellOffen && meineWelt && lage.ichId && (
        <SchnelleAufgabe
          schliessen={() => setSchnellOffen(false)}
          welt={meineWelt}
          ichId={lage.ichId}
          {...(vorbelegt ? { vorbelegt } : {})}
          {...(telefon
            ? {
                ausfuehrlich: () => {
                  setSchnellOffen(false)
                  setAnlegenOffen(true)
                },
              }
            : {})}
        />
      )}
      {ziehenOffen && (
        <WasAlsNaechstes
          rangfolge={lage.naechstes}
          pools={lage.pools}
          ichId={lage.ichId}
          wip={lage.wip}
          wipLimit={lage.wipLimit}
          hinweis={lage.zuvielHinweis}
          schliessen={() => setZiehenOffen(false)}
        />
      )}

      {/* Eine Zeile, keine Karte: eine Randnotiz zum Tag, kein eigener Bereich. */}
      {!ruhig && (
        <div className="mb-3.5">
          <HeuteNichtDa sicht={abwesenheiten} />
        </div>
      )}

      {/* Die heutigen Termine aus dem verbundenen Kalender – nur da, wenn es welche gibt (Z-17). */}
      {/**
       * **Nicht im Kalender** (06.10.2026): dort stehen dieselben Termine im Raster, und auf dem
       * Telefon schob die doppelte Liste mit zwölf Zeilen das Raster einen Bildschirm nach unten.
       */}
      {modus !== 'kalender' && !ruhig && <TermineHeute />}

      {lage.laedt && <Ladehinweis />}

      {/**
       * **⚡ auch in Kalender und Circle** (15.10.2026) – dort ersetzt die Ansicht auf dem Telefon
       * die Liste samt Daumenknopf. Der Abstand darunter hält die letzte Zeile frei.
       */}
      {telefon && (modus === 'kalender' || modus === 'circle') && (
        <>
          <DaumenKnopf
            schnellOeffnen={meineWelt && lage.ichId ? () => setSchnellOffen(true) : null}
            anlegenOeffnen={() => setAnlegenOffen(true)}
          />
        </>
      )}

      {telefon && modus === 'kalender' ? (
        <TagesplanAnsicht vorlauf={lage.naechstes} />
      ) : telefon && modus === 'circle' ? (
        <CirclePipe ansicht={circleAnsicht} setAnsicht={setCircleAnsicht} zusatz={pulsUmfeld} />
      ) : telefon ? (
        <MeinTagTelefon
          lage={lage}
          laufend={laufend}
          nachVorn={setVorn}
          vorhabenOeffnen={setVorhabenDetail}
          ziehenOeffnen={() => setZiehenOffen(true)}
          starten={(id) => starten.mutate({ id })}
          gelesenSetzen={(v) => gelesenSetzen.mutate(v)}
          schnellOeffnen={meineWelt && lage.ichId ? () => setSchnellOffen(true) : null}
          anlegenOeffnen={() => setAnlegenOffen(true)}
        />
      ) : modus === 'circle' ? (
        /**
         * **Der Modus „Circle"** ([Z-19](../../../../docs/phasen/zusatz-19-tagesplan.md) AP 19.9).
         *
         * Nils' Handzeichnung vom 30.09.2026: Winkel = Arbeitswelt, Radius = Zeitfenster, Mitte =
         * in Arbeit. Er steht **neben** den Listen, nicht statt ihrer: Das Rad beantwortet
         * *„woher kommt das und ist noch Platz"*, nicht *„was mache ich als Nächstes"*.
         */
        <CirclePipe ansicht={circleAnsicht} setAnsicht={setCircleAnsicht} zusatz={pulsUmfeld} />
      ) : modus === 'kalender' ? (
        /**
         * **Der Modus „Kalender"** (Z-19 AP 19.6) – der Tag als Zeitstrahl mit echten Terminen,
         * Grundlast und dem, was in die Lücken passt. Was nicht mehr hineinpasst, steht daneben.
         */
        <TagesplanAnsicht vorlauf={lage.naechstes} />
      ) : einfach ? (
        /**
         * **Der Modus „Einfach": eine Zeitachse und eine Spalte daneben**
         * ([Z-12](../../../../docs/phasen/zusatz-12-mein-tag-einfach.md)).
         *
         * Links läuft die Zeit von oben nach unten – **Vorlauf → Jetzt → Erledigt** –, und beide
         * Ränder zeigen zur Mitte: das Nächste steht unten im Vorlauf, das zuletzt Fertige oben
         * im Erledigten. Rechts steht, was auf dieser Achse keinen Platz hat, weil es quer
         * hereinkommt: Nachrichten, der Schirm, das Regal.
         *
         * `lg:sticky` an der rechten Spalte, damit die Nachrichten beim Blättern durch die
         * Achse mitlaufen. Unter `lg` fällt alles untereinander – auf dem Telefon gibt es keine
         * zweite Spalte, und die Achse ist dort die ganze Seite (Z-10).
         */
        <div className="grid grid-cols-1 items-start gap-[22px] lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="flex min-w-0 flex-col gap-[22px]">
            {/**
             * **Hier stehen nur noch Frist und Abnahme** ([E-270](../../../../docs/ENTSCHEIDUNGEN.md#e-270)).
             *
             * Erwähnung und Antwort sind Nachrichten und liegen rechts in ihrer Gruppe; „Sofort"
             * steht ohnehin durch `nachGruppe` auf der untersten Vorlauf-Zeile, mit rotem
             * Rand und „Starten". Was bleibt, hat sonst keinen Ort: eine abgelaufene Frist und
             * eine Abnahme, mit der ich jemand anderen aufhalte.
             */}
            <KlopftAn
              meldungen={lage.klopft.filter((m) => m.art === 'frist' || m.art === 'abnahme')}
              ziehen={(id) => starten.mutate({ id })}
              gelesenSetzen={(v) => gelesenSetzen.mutate(v)}
            />

            <Vorlauf
              rangfolge={lage.naechstes}
              wip={lage.wip}
              wipLimit={lage.wipLimit}
              fokusFuer={lage.fokusFuer}
              wipHinweisText={lage.wipHinweisText}
              hinweis={lage.zuvielHinweis}
              fensterOeffnen={() => setZiehenOffen(true)}
              kalenderOeffnen={(tag) => {
                kalenderTagVormerken(tag)
                setModus('kalender')
              }}
            />

            <Jetzt
              laufend={laufend}
              wip={lage.wip}
              wipLimit={lage.wipLimit}
              wipBereich={lage.wipBereich}
              vorhabenInfo={lage.vorhabenInfo}
              nachVorn={setVorn}
              vorhabenOeffnen={setVorhabenDetail}
              ziehen={() => setZiehenOffen(true)}
            />

            <Geschafft verlauf={lage.verlauf} einfach />
          </div>

          <div className="flex flex-col gap-4 lg:sticky lg:unter-leiste-4">
            <Nachrichten />
            <AufDemSchirm
              angepinnt={lage.angepinnt}
              warte={lage.warte}
              markierungen={lage.markierungen}
              schlafend={lage.schlafend}
            />
            <RuhigGestellt
              wartet={lage.wartet}
              laengstesWarten={lage.laengstesWarten}
              vorratGesamt={lage.vorratGesamt}
              bestandGesamt={lage.bestandGesamt}
              neuFuerDich={lage.neuFuerDich}
              ohneFeed
            />
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-[22px]">
          <KlopftAn
            meldungen={lage.klopft}
            ziehen={(id) => {
              /* Sofort darf das WIP-Limit durchbrechen – ohne Rückfrage, das ist der Zweck.
               Und hier wird **gestartet**, nicht nur genommen: was anklopft, soll laufen. */
              starten.mutate({ id })
            }}
            /* Derselbe Haken wie im Feed darunter – eine Mutation, ein Serverstand. */
            gelesenSetzen={(v) => gelesenSetzen.mutate(v)}
          />

          <Jetzt
            laufend={laufend}
            wip={lage.wip}
            wipLimit={lage.wipLimit}
            wipBereich={lage.wipBereich}
            vorhabenInfo={lage.vorhabenInfo}
            nachVorn={setVorn}
            /* Derselbe Weg ins Detail wie aus Zone 2 – ein Vorhaben, ein Fenster (E-205). */
            vorhabenOeffnen={setVorhabenDetail}
            ziehen={() => setZiehenOffen(true)}
          />

          {/**
           * **Nach „Jetzt", vor den Vorhaben** ([Z-03](../../../../docs/phasen/zusatz-03-auf-dem-schirm.md)).
           *
           * „Jetzt" beantwortet *woran arbeite ich gerade*; „Auf dem Schirm" beantwortet *was darf
           * ich nicht aus den Augen verlieren*. Das Zweite ist kurz und persönlich und gehört
           * deshalb nach oben – aber nicht **vor** die eigentliche Arbeit.
           */}
          <AufDemSchirm
            angepinnt={lage.angepinnt}
            warte={lage.warte}
            markierungen={lage.markierungen}
            schlafend={lage.schlafend}
          />

          {/**
           * **Zwei Spalten nur, wenn links etwas steht** (25.08.2026).
           *
           * `VorhabenSpuren` gibt `null` zurück, solange niemand an einem Vorhaben arbeitet – bei
           * 7 von 67 Aufgaben mit Vorhaben ist das der Normalfall. Bliebe das Raster fest auf
           * `1.6fr_1fr`, stünde „Als Nächstes" dann in einem Drittel der Breite und daneben
           * gähnte Leere. Also: eine Spalte, wenn die Hauptspur schweigt.
           */}
          <div
            className={cn(
              'grid grid-cols-1 items-start gap-[22px]',
              lage.spuren.length > 0 && 'lg:grid-cols-[1.6fr_1fr]',
            )}
          >
            {/* Der Name führt hinein – dasselbe Detailfenster wie in der Vorhaben-Ansicht (E-205). */}
            <VorhabenSpuren
              spuren={lage.spuren}
              ohneVorhaben={lage.ohneVorhaben}
              vorhabenOeffnen={setVorhabenDetail}
            />

            <AlsNaechstes
              rangfolge={lage.naechstes}
              wip={lage.wip}
              wipLimit={lage.wipLimit}
              fokusFuer={lage.fokusFuer}
              wipHinweisText={lage.wipHinweisText}
              hinweis={lage.zuvielHinweis}
              fensterOeffnen={() => setZiehenOffen(true)}
            />
          </div>

          <Geschafft verlauf={lage.verlauf} />

          <RuhigGestellt
            wartet={lage.wartet}
            laengstesWarten={lage.laengstesWarten}
            vorratGesamt={lage.vorratGesamt}
            bestandGesamt={lage.bestandGesamt}
            neuFuerDich={lage.neuFuerDich}
          />
        </div>
      )}
      {/* Platz für den ⚡-Knopf in Kalender und Circle, damit die letzte Zeile frei bleibt. */}
      {telefon && (modus === 'kalender' || modus === 'circle') && (
        <div aria-hidden="true" className="h-20" />
      )}

      {vorhabenDetail && (
        <VorhabenDetail vorhabenId={vorhabenDetail} schliessen={() => setVorhabenDetail(null)} />
      )}
    </>
  )
}

/**
 * **Der Ansichtsschalter auf dem Telefon** (Nils, 30.09.2026: *„Mobil gibt es gar keinen
 * Schalter um die Ansichten zu erreichen, das bitte noch einfügen"*).
 *
 * Kalender und Circle waren auf dem Telefon **gar nicht erreichbar**: Dort übernimmt
 * {@link MeinTagTelefon} mit seinen vier Reitern, und der Modus-Umschalter des Rechners galt
 * ausdrücklich nicht (Z-16).
 *
 * ## Warum ein Schalter im Kopf und keine zwei weiteren Reiter
 *
 * Die Leiste unten ist ein `grid-cols-4`. Aus vier Reitern sechs zu machen hieße auf einem
 * 358 px breiten Gerät **60 px je Spalte** – „Nachrichten" passt dort in 10,5 px Schrift nicht
 * mehr hinein, und die Reiter umzubenennen wäre eine Änderung an vier Dingen, um zwei
 * hinzuzufügen. Der Kopf hat den Platz dagegen in der Waagerechten schon: eine Zeile, drei
 * Symbole, keine zusätzliche Höhe.
 *
 * ## Drei Zustände, nicht vier
 *
 * „Einfach" und „Ausführlich" sind auf dem Telefon dasselbe – die Reiter sind dort die
 * Anordnung (Z-16). Deshalb steht hier **Liste** für beide. Der Modus liegt je Gerät im
 * `localStorage`, ein Druck auf „Liste" stellt also nicht die Wahl am Schreibtisch um.
 *
 * Nur Symbole, mit `aria-label`: Drei Wörter neben dem Tagesziel wären genau die Breite, die
 * auf dem Telefon nicht da ist.
 */
function TelefonAnsichtSchalter({
  modus,
  setModus,
}: {
  modus: Tagesmodus
  setModus: (modus: Tagesmodus) => void
}) {
  const wahl: { wert: Tagesmodus; name: string; Symbol: typeof List; aktiv: boolean }[] = [
    {
      wert: 'einfach',
      name: 'Liste',
      Symbol: List,
      aktiv: modus !== 'kalender' && modus !== 'circle',
    },
    { wert: 'kalender', name: 'Kalender', Symbol: CalendarDays, aktiv: modus === 'kalender' },
    { wert: 'circle', name: 'Circle', Symbol: Target, aktiv: modus === 'circle' },
  ]
  return (
    <div className="flex items-center gap-0.5 rounded-[10px] border border-line2 bg-surface p-0.5">
      {wahl.map(({ wert, name, Symbol, aktiv }) => (
        <button
          key={wert}
          type="button"
          aria-label={name}
          aria-pressed={aktiv}
          /* Ein Druck auf die schon gewählte Liste lässt „Ausführlich" stehen (Z-22). */
          onClick={() => !aktiv && setModus(wert)}
          className={
            aktiv
              ? 'cursor-pointer rounded-lg bg-[#f3f2ef] p-1.5 text-accent-deep'
              : 'cursor-pointer rounded-lg p-1.5 text-ink2'
          }
        >
          <Symbol className="size-[18px]" />
        </button>
      ))}
    </div>
  )
}

/**
 * **Der Umschalter über der Ansicht** ([Z-12](../../../../docs/phasen/zusatz-12-mein-tag-einfach.md)).
 *
 * Zwei Wörter, keine Einstellung in einem Menü: was er tut, sieht man sofort, und was er
 * rückgängig macht, steht direkt daneben. Er ist bewusst das **erste** Element der Kopfzeile –
 * wer die Ansicht zum ersten Mal sieht und sie nicht mag, soll nicht suchen müssen.
 *
 * `aria-pressed` statt eines Radiofelds: für die Vorlesehilfe sind es zwei Knöpfe, von denen
 * einer gedrückt ist – dieselbe Bauform wie beim Zeitraum im Feed-Fenster.
 */
function ModusSchalter({
  modus,
  setModus,
}: {
  modus: Tagesmodus
  setModus: (modus: Tagesmodus) => void
}) {
  const wahl: { wert: Tagesmodus; text: string; erklaerung: string }[] = [
    {
      wert: 'einfach',
      text: 'Einfach',
      erklaerung: 'Drei Zonen: was kommt, was läuft, was fertig ist – Nachrichten daneben.',
    },
    {
      wert: 'ausfuehrlich',
      text: 'Ausführlich',
      erklaerung: 'Alle Zonen einzeln, mit Vorhaben-Spuren und dem Feed unten.',
    },
    {
      wert: 'kalender',
      text: 'Kalender',
      erklaerung: 'Der Tag als Zeitstrahl: Termine, Grundlast, und was in die Lücken passt.',
    },
    {
      wert: 'circle',
      text: 'Circle',
      erklaerung: 'Arbeitswelt × Zeitfenster als Rad, in der Mitte was läuft.',
    },
  ]

  const { istStandard, standardSetzen } = useStandardansicht(modus)

  return (
    <div className="flex items-center gap-2">
      <div className="flex gap-[2px] rounded-full bg-[#eeedea] p-[3px]" role="group">
        {wahl.map((w) => (
          <button
            key={w.wert}
            type="button"
            onClick={() => setModus(w.wert)}
            aria-pressed={modus === w.wert}
            title={w.erklaerung}
            className={cn(
              'cursor-pointer rounded-full px-[13px] py-1.5 text-[12.5px] font-semibold transition-colors',
              modus === w.wert ? 'bg-surface text-ink shadow-task' : 'text-ink2 hover:text-ink',
            )}
          >
            {w.text}
          </button>
        ))}
      </div>
      {/*
       * **Ein Stern, kein zweiter Schalter.** Der Knopf beantwortet „soll das meine Vorgabe
       * sein?" für die *gerade sichtbare* Ansicht – deshalb steht er daneben und nicht in einem
       * Menü. Nochmal drücken nimmt die Vorgabe wieder weg; ohne diesen Weg wäre „ich will keine"
       * nicht ausdrückbar.
       */}
      <button
        type="button"
        disabled={standardSetzen.isPending}
        onClick={() => standardSetzen.mutate(istStandard ? null : modus)}
        aria-pressed={istStandard}
        title={
          istStandard
            ? 'Ist deine Standardansicht – nochmal drücken hebt das auf'
            : 'Diese Ansicht als Standard für neue Geräte speichern'
        }
        className={cn(
          'cursor-pointer rounded-full px-2 py-1 text-[13px] leading-none transition-colors',
          istStandard ? 'text-[#8a6100]' : 'text-muted hover:text-ink2',
        )}
      >
        {istStandard ? '★' : '☆'}
        <span className="sr-only">Als Standardansicht speichern</span>
      </button>
    </div>
  )
}

/**
 * **Die Standardansicht am Konto** (Nils, 30.09.2026: *„Jeder User kann sich selber seine
 * «Standard» ansicht dann speichern"*).
 *
 * Sie überschreibt **nicht**, was auf diesem Gerät eingestellt ist – sie greift, wenn ein Gerät
 * nichts weiß. Die Regel steht in `tagesmodusWaehlen` in `shared`; hier hängt nur der Knopf.
 * Ein Hook, weil der Stern seit Z-22 an zwei Stellen steht: neben dem breiten Schalter und im
 * Menü des kompakten.
 */
function useStandardansicht(modus: Tagesmodus) {
  const queryClient = useQueryClient()
  const { data: sitzung } = useQuery(sitzungQuery)
  const standardSetzen = useMutation({
    mutationFn: (m: Tagesmodus | null) => trpc.people.tagesmodusSetzen.mutate({ modus: m }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['auth', 'me'] }),
  })
  return { istStandard: sitzung?.meintagStandard === modus, standardSetzen }
}

/** Schließt ein kleines Menü bei Klick daneben und bei Esc. */
function useMenueSchliessen(kasten: RefObject<HTMLElement | null>, offen: boolean, zu: () => void) {
  useEffect(() => {
    if (!offen) return
    const weg = (e: MouseEvent | KeyboardEvent) => {
      if (
        e instanceof KeyboardEvent
          ? e.key === 'Escape'
          : !kasten.current?.contains(e.target as Node)
      )
        zu()
    }
    document.addEventListener('mousedown', weg)
    document.addEventListener('keydown', weg)
    return () => {
      document.removeEventListener('mousedown', weg)
      document.removeEventListener('keydown', weg)
    }
  }, [kasten, offen, zu])
}

/**
 * **Der Ansichtsschalter, wenn es eng wird** (Z-22, 20.10.2026, Nils: *„da oben müssten wir denke
 * ich aufräumen … wenn das display schmaler ist?"*).
 *
 * Unter 1400 px und im Puls: die drei Symbole des Telefons (Liste · Kalender · Circle) und ein
 * kleines Menü daneben. Darin steht, was sonst als Wort im breiten Schalter stünde – „Einfach"
 * oder „Ausführlich" für die Liste – und der Stern für die Standardansicht. Spart rund 250 px.
 */
function AnsichtSchalterKompakt({
  modus,
  setModus,
}: {
  modus: Tagesmodus
  setModus: (modus: Tagesmodus) => void
}) {
  const [offen, setOffen] = useState(false)
  const kasten = useRef<HTMLDivElement>(null)
  const zu = useCallback(() => setOffen(false), [])
  useMenueSchliessen(kasten, offen, zu)
  const { istStandard, standardSetzen } = useStandardansicht(modus)
  return (
    <div ref={kasten} className="relative flex items-center gap-1">
      <TelefonAnsichtSchalter modus={modus} setModus={setModus} />
      <button
        type="button"
        onClick={() => setOffen((o) => !o)}
        aria-expanded={offen}
        aria-label="Weitere Ansichtseinstellungen"
        title="Einfach oder Ausführlich, Standardansicht"
        className="cursor-pointer rounded-lg px-1.5 py-1.5 text-[12px] text-muted hover:bg-[#f3f2ef] hover:text-ink2"
      >
        ▾
      </button>
      {offen && (
        <div
          role="menu"
          className="absolute top-full left-0 z-30 mt-1.5 w-[230px] rounded-card border border-line2 bg-surface p-1.5 shadow-card"
        >
          {(
            [
              ['einfach', 'Liste einfach', 'Drei Zonen und Nachrichten'],
              ['ausfuehrlich', 'Liste ausführlich', 'Alle Zonen, Vorhaben-Spuren, Feed'],
            ] as const
          ).map(([wert, text, erklaerung]) => (
            <button
              key={wert}
              type="button"
              role="menuitemradio"
              aria-checked={modus === wert}
              onClick={() => {
                setModus(wert)
                setOffen(false)
              }}
              className="flex w-full cursor-pointer items-start gap-2 rounded-lg px-2.5 py-1.5 text-left hover:bg-[#f3f2ef]"
            >
              <span className="w-3 text-accent-deep">{modus === wert ? '✓' : ''}</span>
              <span>
                <b className="block text-[13px] font-medium">{text}</b>
                <span className="text-[11.5px] text-muted">{erklaerung}</span>
              </span>
            </button>
          ))}
          <div className="my-1 border-t border-line" />
          <button
            type="button"
            role="menuitemcheckbox"
            aria-checked={istStandard}
            disabled={standardSetzen.isPending}
            onClick={() => standardSetzen.mutate(istStandard ? null : modus)}
            className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-[13px] hover:bg-[#f3f2ef]"
          >
            <span className={istStandard ? 'text-[#8a6100]' : 'text-muted'}>
              {istStandard ? '★' : '☆'}
            </span>
            {istStandard ? 'Ist deine Standardansicht' : 'Diese Ansicht als Standard'}
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * **Ein Anlege-Knopf statt zwei** (Z-22, 20.10.2026). Der Hauptklick öffnet die Schnelle Aufgabe –
 * die häufigere Handlung, und die ohne Formular. Der Pfeil daneben bietet beide an. Ohne
 * persönliche Welt (Z-15) gibt es keine Schnelle Aufgabe; dann bleibt ein schlichter Knopf.
 */
function AnlegeKnopf({
  schnell,
  anlegen,
  weltName,
}: {
  schnell: (() => void) | null
  anlegen: () => void
  weltName: string | null
}) {
  const [offen, setOffen] = useState(false)
  const kasten = useRef<HTMLDivElement>(null)
  const zu = useCallback(() => setOffen(false), [])
  useMenueSchliessen(kasten, offen, zu)
  if (!schnell) {
    return (
      <Button variant="ghost" onClick={anlegen}>
        + Aufgabe
      </Button>
    )
  }
  return (
    <div ref={kasten} className="relative inline-flex">
      <Button
        variant="ghost"
        onClick={schnell}
        aria-label="+ Aufgabe – Schnelle Aufgabe"
        className="rounded-r-none"
        title={`Schnelle Aufgabe – landet in ${weltName ?? 'deiner Welt'}, du bist Bearbeiter.`}
      >
        + Aufgabe
      </Button>
      <Button
        variant="ghost"
        onClick={() => setOffen((o) => !o)}
        aria-expanded={offen}
        aria-label="Anlegen: weitere Wege"
        className="-ml-px rounded-l-none px-2"
      >
        ▾
      </Button>
      {offen && (
        <div
          role="menu"
          className="absolute top-full right-0 z-30 mt-1.5 w-[250px] rounded-card border border-line2 bg-surface p-1.5 shadow-card"
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOffen(false)
              schnell()
            }}
            className="block w-full cursor-pointer rounded-lg px-2.5 py-1.5 text-left hover:bg-[#f3f2ef]"
          >
            <b className="block text-[13px] font-medium">⚡ Schnelle Aufgabe</b>
            <span className="text-[11.5px] text-muted">
              Titel, Fällig, Prio – in {weltName ?? 'deiner Welt'}
            </span>
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOffen(false)
              anlegen()
            }}
            className="block w-full cursor-pointer rounded-lg px-2.5 py-1.5 text-left hover:bg-[#f3f2ef]"
          >
            <b className="block text-[13px] font-medium">Ausführlich anlegen …</b>
            <span className="text-[11.5px] text-muted">Welt, Pool, Bearbeiter, Beschreibung</span>
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * Was aus „Teilen nach Work" wird: der geteilte Titel als Titel (sonst die erste Zeile des
 * Texts), der Rest samt Adresse als Notiz. Nichts wird gekürzt – der Dialog zeigt es zum Prüfen.
 */
function geteiltesVorbelegen(suche: Record<string, unknown>): { titel: string; notiz: string } {
  const wert = (k: string) => (suche[k] === undefined ? '' : String(suche[k]).trim())
  const text = wert('text')
  const url = wert('url')
  const titel = wert('titel') || text.split('\n')[0]?.trim() || url
  const notiz = [titel === text ? '' : text, url].filter(Boolean).join('\n\n')
  return { titel, notiz }
}

export function Ladehinweis() {
  return <div className="py-4 text-[13px] text-muted">Lädt …</div>
}

function kalenderwoche(datum: Date): number {
  const d = new Date(Date.UTC(datum.getFullYear(), datum.getMonth(), datum.getDate()))
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7))
  const jahresbeginn = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
  return Math.ceil(((d.getTime() - jahresbeginn.getTime()) / 86_400_000 + 1) / 7)
}
